import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import useStore from "../store/useStore";

const BannerSkeleton = () => (
  <section className="mx-auto w-full max-w-[1220px] px-2 py-2 sm:px-3">
    <div className="flex gap-3">
      <div className="h-[140px] w-1/2 animate-pulse rounded-[20px] bg-gray-200 sm:h-[130px] md:h-[150px] lg:h-[170px]" />
      <div className="h-[140px] w-1/2 animate-pulse rounded-[20px] bg-gray-200 sm:h-[130px] md:h-[150px] lg:h-[170px]" />
    </div>
  </section>
);

const GRADIENTS = [
  "from-[#1a6b3c] via-[#2E7B52] to-[#1F5A3A]",
  "from-[#b45309] via-[#d97706] to-[#92400e]",
  "from-[#1e40af] via-[#3b82f6] to-[#1d4ed8]",
  "from-[#7c3aed] via-[#8b5cf6] to-[#6d28d9]",
  "from-[#be123c] via-[#e11d48] to-[#9f1239]",
];

const BannerCard = ({ banner, index, imgErr, setImgErr, onClick }) => {
  const hasImage = banner.image && !imgErr[banner.id ?? index];
  const isClickable = Boolean(banner.url);
  const gradient = GRADIENTS[index % GRADIENTS.length];

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : -1}
      onClick={() => onClick(banner)}
      onKeyDown={(event) => {
        if (isClickable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick(banner);
        }
      }}
      className={`relative h-[140px] w-full overflow-hidden rounded-[20px] shadow-md transition-shadow hover:shadow-xl sm:h-[130px] md:h-[150px] lg:h-[170px] ${
        isClickable ? "cursor-pointer" : "cursor-default"
      }`}
    >
      {hasImage ? (
        <>
          <img
            src={banner.image}
            alt={banner.title || `Banner ${index + 1}`}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 hover:scale-105"
            onError={() =>
              setImgErr((previous) => ({
                ...previous,
                [banner.id ?? index]: true,
              }))
            }
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        </>
      ) : (
        <>
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              background:
                "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.1) 0%, transparent 40%)",
            }}
          />
        </>
      )}

      {banner.title && (
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="line-clamp-1 text-sm font-extrabold leading-snug text-white drop-shadow">
            {banner.title}
          </p>
        </div>
      )}
    </div>
  );
};

const BannerCarousel = () => {
  const { banners, banners_loading, fetchBanners } = useStore();
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  useEffect(() => {
    fetchBanners();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (banners_loading && banners.length === 0) return <BannerSkeleton />;
  if (!banners.length) return null;

  const handleBannerClick = (banner) => {
    if (banner.url) navigate(banner.url);
  };
  const canLoop = banners.length > 2;

  return (
    <section className="mx-auto hidden w-full max-w-[1220px] px-2 py-2 sm:block sm:px-3">
      <div className="relative">
        <Swiper
          modules={[Autoplay]}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          slidesPerView={2}
          spaceBetween={12}
          loop={canLoop}
          autoplay={
            canLoop
              ? {
                  delay: 3500,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          speed={700}
          className="!overflow-hidden !pb-1"
        >
          {banners.map((banner, index) => (
            <SwiperSlide key={banner.id ?? index}>
              <BannerCard
                banner={banner}
                index={index}
                imgErr={imgErr}
                setImgErr={setImgErr}
                onClick={handleBannerClick}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {canLoop && (
          <>
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous banners"
              className="absolute -left-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition hover:bg-white hover:shadow-lg active:scale-90 sm:-left-4 sm:h-9 sm:w-9"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-gray-700">
                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next banners"
              className="absolute -right-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition hover:bg-white hover:shadow-lg active:scale-90 sm:-right-4 sm:h-9 sm:w-9"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-gray-700">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </button>
          </>
        )}
      </div>

      {canLoop && (
        <div className="mt-2.5 flex justify-center gap-1.5">
          {banners.map((banner, index) => (
            <button
              key={banner.id ?? index}
              type="button"
              onClick={() => swiperRef.current?.slideToLoop(index)}
              aria-label={`Go to banner ${index + 1}`}
              aria-current={index === activeIndex ? "true" : undefined}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? "w-5 bg-green-600"
                  : "w-1.5 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default BannerCarousel;
